import { useEffect, useState, useCallback } from "react";
import { X, Cookie, ShieldCheck } from "lucide-react";
import { useLang } from "@/lib/i18n";

/*
 * Bannière consentement cookies conforme CNIL / RGPD.
 * - 3 boutons : Tout accepter / Tout refuser / Personnaliser (équilibre visuel accepter <-> refuser)
 * - Consentement granulaire (analytics, ads)
 * - Persistance 13 mois maximum (durée CNIL)
 * - Aucun tag Google chargé tant que consentement n'est pas donné
 * - Bouton "Gestion des cookies" ré-ouvrable depuis n'importe quelle page (event 'empco:open-cookies')
 */

const STORAGE_KEY = "empco_cookie_consent_v1";
const MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000; // 13 mois CNIL
const GOOGLE_ANALYTICS_ID = "G-XG2YTR3YM4";     // GA4 (mesure d'audience)
const GOOGLE_ADS_ID = "AW-18445277356";         // Google Ads (conversion / retargeting)

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed;
  } catch { return null; }
}

function writeConsent(analytics, ads) {
  const payload = { ts: Date.now(), analytics: !!analytics, ads: !!ads };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  return payload;
}

function injectGoogleTag(analytics, ads) {
  if (window.__empcoGtagLoaded) return;
  window.__empcoGtagLoaded = true;
  // 1) Initialise dataLayer + defaults AVANT chargement gtag.js (best practice Consent Mode v2)
  window.dataLayer = window.dataLayer || [];
  function gtag(){ window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    wait_for_update: 500,
  });
  gtag("js", new Date());
  // GA4 — mesure d'audience (gouvernée par analytics_storage)
  gtag("config", GOOGLE_ANALYTICS_ID, { anonymize_ip: true });
  // Google Ads — conversion/retargeting (gouvernée par ad_storage)
  gtag("config", GOOGLE_ADS_ID);
  // 2) Ensuite charge le script (async). Un seul gtag.js suffit pour tous les IDs.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(s);
  // 3) Applique consentement effectif
  applyConsentMode(analytics, ads);
}

function applyConsentMode(analytics, ads) {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: ads ? "granted" : "denied",
    ad_user_data: ads ? "granted" : "denied",
    ad_personalization: ads ? "granted" : "denied",
  });
}

function applyPosthog(analytics) {
  // PostHog est aussi soumis à consentement CNIL (session recording = tracker)
  try {
    if (!window.posthog) return;
    if (analytics) {
      window.posthog.opt_in_capturing();
    } else {
      window.posthog.opt_out_capturing();
    }
  } catch { /* posthog non chargé, ignoré */ }
}

export default function CookieConsent() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  const applyAndClose = useCallback((a, ad) => {
    writeConsent(a, ad);
    if (a || ad) {
      injectGoogleTag(a, ad);
    } else {
      applyConsentMode(false, false);
    }
    applyPosthog(a);
    setOpen(false);
    setShowPanel(false);
  }, []);

  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      // Restaure Google tag si consenti auparavant
      if (existing.analytics || existing.ads) {
        injectGoogleTag(existing.analytics, existing.ads);
      }
      applyPosthog(existing.analytics);
    } else {
      setOpen(true);
    }
    const reopen = () => { const cur = readConsent(); setAnalytics(!!cur?.analytics); setAds(!!cur?.ads); setShowPanel(true); setOpen(true); };
    window.addEventListener("empco:open-cookies", reopen);
    return () => window.removeEventListener("empco:open-cookies", reopen);
  }, []);

  if (!open) return null;

  if (showPanel) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="cookie-panel">
        <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto">
          <div className="flex items-start justify-between p-6 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{t("cookie.panel.title")}</h3>
                <p className="text-xs text-slate-500">{t("cookie.panel.sub")}</p>
              </div>
            </div>
            <button onClick={() => setShowPanel(false)} className="text-slate-400 hover:text-slate-700" data-testid="cookie-panel-close">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t("cookie.cat.necessary.title")}</p>
                  <p className="text-xs text-slate-500">{t("cookie.cat.necessary.desc")}</p>
                </div>
                <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">{t("cookie.cat.always")}</span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <p className="text-sm font-semibold text-slate-900">{t("cookie.cat.analytics.title")}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("cookie.cat.analytics.desc")}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-testid="cookie-toggle-analytics">
                  <input type="checkbox" className="sr-only peer" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-200 peer-checked:bg-emerald-500 rounded-full peer-focus:ring-2 peer-focus:ring-emerald-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                </label>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 pr-4">
                  <p className="text-sm font-semibold text-slate-900">{t("cookie.cat.ads.title")}</p>
                  <p className="text-xs text-slate-500 mt-1">{t("cookie.cat.ads.desc")}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-testid="cookie-toggle-ads">
                  <input type="checkbox" className="sr-only peer" checked={ads} onChange={(e) => setAds(e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-200 peer-checked:bg-emerald-500 rounded-full peer-focus:ring-2 peer-focus:ring-emerald-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
                </label>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed pt-2">
              {t("cookie.panel.legal")}
            </p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row gap-3 p-6 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
            <button onClick={() => applyAndClose(false, false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition" data-testid="cookie-panel-refuse">
              {t("cookie.btn.refuse")}
            </button>
            <button onClick={() => applyAndClose(analytics, ads)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 transition" data-testid="cookie-panel-save">
              {t("cookie.btn.save")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] p-4 md:p-6" data-testid="cookie-banner">
      <div className="max-w-5xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Cookie className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-semibold text-slate-900 mb-1">{t("cookie.banner.title")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {t("cookie.banner.body")} <a href="/mentions-legales" className="text-emerald-600 underline hover:text-emerald-700">{t("cookie.banner.learn")}</a>.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <button onClick={() => applyAndClose(false, false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition" data-testid="cookie-refuse">
              {t("cookie.btn.refuse")}
            </button>
            <button onClick={() => { setAnalytics(false); setAds(false); setShowPanel(true); }} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-full hover:bg-slate-100 transition" data-testid="cookie-customize">
              {t("cookie.btn.customize")}
            </button>
            <button onClick={() => applyAndClose(true, true)} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-full hover:bg-emerald-700 transition" data-testid="cookie-accept">
              {t("cookie.btn.accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new Event("empco:open-cookies"));
}
